import { useState, useRef, useEffect } from "react";
import { Calendar } from "lucide-react";
import { format, subDays, subMonths, startOfMonth, endOfMonth, isAfter, isBefore } from "date-fns";

const DateRangePicker = ({ startDate, endDate, onDateChange, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStart, setTempStart] = useState(startDate);
  const [tempEnd, setTempEnd] = useState(endDate);
  const containerRef = useRef(null);

  // Sync temp values when props change
  useEffect(() => {
    setTempStart(startDate);
    setTempEnd(endDate);
  }, [startDate, endDate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Preset ranges
  const presets = [
    { label: "Last 7 days", getValue: () => ({ start: subDays(new Date(), 7), end: new Date() }) },
    { label: "Last 30 days", getValue: () => ({ start: subDays(new Date(), 30), end: new Date() }) },
    { label: "Last 3 months", getValue: () => ({ start: subMonths(new Date(), 3), end: new Date() }) },
    { label: "Last 6 months", getValue: () => ({ start: subMonths(new Date(), 6), end: new Date() }) },
    { label: "This month", getValue: () => ({ start: startOfMonth(new Date()), end: endOfMonth(new Date()) }) },
    { label: "Last month", getValue: () => ({ start: startOfMonth(subMonths(new Date(), 1)), end: endOfMonth(subMonths(new Date(), 1)) }) },
  ];

  const handlePresetClick = (preset) => {
    const { start, end } = preset.getValue();
    setTempStart(start);
    setTempEnd(end);
    onDateChange({ startDate: start, endDate: end });
    setIsOpen(false);
  };

  const handleDateInputChange = (type, value) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return;

    if (type === "start") {
      setTempStart(date);
      if (isAfter(date, tempEnd)) {
        setTempEnd(date);
      }
    } else {
      setTempEnd(date);
      if (isBefore(date, tempStart)) {
        setTempStart(date);
      }
    }
  };

  const handleApply = () => {
    onDateChange({ startDate: tempStart, endDate: tempEnd });
    setIsOpen(false);
  };

  const handleClear = () => {
    const defaultStart = subMonths(new Date(), 6);
    const defaultEnd = new Date();
    setTempStart(defaultStart);
    setTempEnd(defaultEnd);
    onDateChange({ startDate: defaultStart, endDate: defaultEnd });
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        <span className="text-sm text-gray-700 dark:text-gray-300">
          {format(startDate, "MMM d, yyyy")} - {format(endDate, "MMM d, yyyy")}
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute left-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-[9999]">
          {/* Presets */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Quick Select</p>
            <div className="grid grid-cols-2 gap-2">
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => handlePresetClick(preset)}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Range */}
          <div className="p-3">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Custom Range</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Start Date</label>
                <input
                  type="date"
                  value={format(tempStart, "yyyy-MM-dd")}
                  onChange={(e) => handleDateInputChange("start", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">End Date</label>
                <input
                  type="date"
                  value={format(tempEnd, "yyyy-MM-dd")}
                  onChange={(e) => handleDateInputChange("end", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex justify-between">
            <button
              onClick={handleClear}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              Reset
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
